import Script from 'next/script';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { sendEvent } from 'lib/gtag';
import { getFromSessionStorage } from 'lib/sessionStorage';
import { noop, waitForGlobalWindowVar } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import { postSignIn } from 'services/user';

// You can see all available style props here: https://developer.apple.com/documentation/sign_in_with_apple/displaying_sign_in_with_apple_buttons_on_the_web
const APPLE_BUTTON_STYLES = {
  'data-border': 'true',
  'data-color': 'black',
  'data-locale': 'pt_BR',
  'data-logo-size': 'small',
  'data-mode': 'logo-only',
  'data-type': 'sign-in',
};

// You can see more about the required configs here: https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
const APPLE_CLIENT_SDK_CONFIG = {
  clientId: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID,
  scope: 'name email',
  redirectURI: process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI,
  state: 'signin',
  nonce: 'nonce',
  usePopup: true,
};

const AppleLogin = ({
  gaEventCategory,
  onSuccess = noop,
  onFailure = noop,
}) => {
  const { login } = useUser();

  useEffect(() => {
    const onSignInSuccess = async (event) => {
      const { code: oauthToken } = event.detail.authorization;

      try {
        const utmCampaign = getFromSessionStorage('utm_campaign');
        sendEvent({
          action: 'login_appleid_submit',
          category: gaEventCategory,
        });

        const { token, isNewUser = false } = await postSignIn({
          oauthToken,
          oauthDomain: 'apple',
          /**
           * usually we don't need to specific the platform to make any API
           * request, but due some Apple peculiarities, the API needs know the
           * correct platform to validate the `oauthToken` correctly.
           */
          oauthPlatform: 'web',
          utmCampaign,
        });

        const gaEventActionPrefix = isNewUser ? 'signup' : 'login';

        login({ token });
        sendEvent({
          action: `${gaEventActionPrefix}_appleid_success`,
          category: gaEventCategory,
        });
        onSuccess();
      } catch (err) {
        onFailure(err);
      }
    };

    const onSignInFailure = async (event) => {
      const { captureException } = await import('@sentry/nextjs');

      captureException(event.detail.error);
    };

    document.addEventListener('AppleIDSignInOnSuccess', onSignInSuccess);
    document.addEventListener('AppleIDSignInOnFailure', onSignInFailure);

    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', onSignInSuccess);
      document.removeEventListener('AppleIDSignInOnFailure', onSignInFailure);
    };
  }, []);

  return (
    <>
      <button
        className='size-10 rounded-3 bg-rd-neutral-800 [&_div[role="button"]]:!h-[42px]'
        id="appleid-signin"
        onClick={() => {
          sendEvent({
            category: gaEventCategory,
            action: 'login_apple_submit',
          });
        }}
        {...APPLE_BUTTON_STYLES}
      />
      <Script
        id="apple-identify-services"
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
        onReady={() => {
          waitForGlobalWindowVar('AppleID', () => {
            window.AppleID.auth.init(APPLE_CLIENT_SDK_CONFIG);
          });
        }}
      />
    </>
  );
};

AppleLogin.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
};

export default AppleLogin;
